// Copyright (c) 2026 AesirX.io Ltd. All rights reserved.
// This source code is proprietary and confidential.
// See LICENSE.md for licensing terms.
// ── What a scheme classifies ─────────────────────────────────────
/**
 * The subject of a scheme.
 *
 * Two kinds shipped in 62.4.0. The container was left deliberately open, with
 * the next one named in advance: *"an AI-risk scheme is the same shape, which is
 * the convergence point the 64.x abstraction of Sprint 62.3.0 can aim at rather
 * than inventing a third one."*
 *
 * Sprint 64.7.0 takes that convergence point. `ai_system` is the third kind, and
 * the AI System Register reads its instrument through this registry instead of
 * through `DECISION_33_FRAMEWORK_CODE`. The mapping needed no new shapes:
 * Decision 33's appendix items are `ClassificationCategory` (`appendix_item` is
 * `list_item`, its opaque sector is `applies_to_scopes`), and Decree 142's three
 * tiers are `ClassificationTier[]`.
 *
 * That last one is the load-bearing part. Tier codes and their ordering are
 * declared **per scheme**, so a regulator whose scheme has a *prohibited* tier
 * expresses it by declaring one — no union in `src/` grows a value that means a
 * different legal thing under each instrument, and no global rank has to order
 * two regulators' tiers against each other.
 */
export const CLASSIFICATION_SCHEME_KINDS = [
    'data_asset',
    'information_system',
    'ai_system',
];
/**
 * How a category can be matched.
 *
 * The distinction is legal rather than technical, and it is the same in every
 * jurisdiction. `subject_matter_declaration` means only a person can say whether
 * a record falls inside a natural-language description — matching by keyword
 * would manufacture a legal conclusion out of string overlap and present it with
 * the confidence of a real one. `quantitative_threshold` means the instrument
 * states a number and the platform may apply it. `incorporation_by_reference`
 * means the entry is a pointer at another part of the same list rather than a
 * category (Vietnam's Decision 20 Mục II item 1 is one; it will not be the last).
 */
export const CLASSIFICATION_MATCH_MODES = [
    'subject_matter_declaration',
    'quantitative_threshold',
    'incorporation_by_reference',
];
/**
 * How a scheme combines the levels of a composite subject.
 *
 * `highest_component` is Vietnam's rule for information systems (Nghị định
 * 85/2016 Điều 5 khoản 2), but it is not universal and must not be assumed —
 * hence a declared value. `none` means the scheme states no aggregation rule and
 * the platform applies none.
 */
export const CLASSIFICATION_AGGREGATION_RULES = ['highest_component', 'none'];
// ── Transitional relationships between schemes (Sprint 62.26.0) ──
/**
 * Which side of the transition the DECLARING scheme is on.
 *
 * `succeeds` — the declaring scheme is the successor and `related_scheme_id`
 * names the predecessor. `succeeded_by` — the reverse. Both are accepted so a
 * pack can author the relationship on whichever instrument states it; the
 * resolver derives predecessor and successor from this and nothing else.
 */
export const SCHEME_TRANSITION_DIRECTIONS = ['succeeds', 'succeeded_by'];
/**
 * What a transitional window is anchored to.
 *
 * One member today: the effective date of an INSTALLED framework, named by
 * code. A literal date is deliberately not a member — a window anchored to a
 * date the pack typed would be a date nobody can trace to an instrument, and
 * an anchor that is not installed resolves to *no anchor declared* rather than
 * to any substitute (AC7).
 */
export const SCHEME_TRANSITION_ANCHOR_KINDS = ['framework_effective_date'];
/** The units a transitional window may be declared in — the pack's own unit. */
export const SCHEME_TRANSITION_WINDOW_UNITS = ['days', 'months', 'years'];
/**
 * Sprint 63.33.0 (AC1) — how the declared period ENDS, as the pack states it.
 *
 * The window opens on the anchor date and runs for the declared period; "anchor
 * plus period" is the calendar date reached by adding the period in its own
 * unit (month ends clamp). The two members differ only in which side of that
 * date the window closes on:
 *
 * - `closed_from_anchor_plus_period` — **the default**, and the only reading
 *   the platform applied before this field existed. The window is open up to
 *   the day before anchor plus period and reads closed FROM anchor plus period.
 *   Anchor 1 July, 12 months: open through 30 June, closed from 1 July.
 * - `open_through_anchor_plus_period` — anchor plus period is the LAST open
 *   day; the window reads closed from the following day. This is a counting
 *   rule under which the day of the event is not counted and a period ends at
 *   the end of its corresponding day. Anchor 1 July, 12 months: open through
 *   1 July of the following year, closed from 2 July.
 *
 *   **It does not move an end day that falls on a weekend or a public
 *   holiday.** The last open day is anchor plus period on the calendar, never
 *   rolled to the next working day — no organisation calendar is read. A pack
 *   must therefore NOT declare this member for a window whose governing rule
 *   rolls a non-working last day forward and whose last day would roll: the
 *   window would read closed while the period is still running. Such a window
 *   needs a member of its own (with the calendar) before it can be declared.
 *
 * Declared per pack, on the window, by whoever read the instrument and the
 * counting rule it falls under. The platform never infers it: a window that
 * declares nothing is read with the default, and the resolved window states
 * which rule it applied and whether the pack declared it. Generic by
 * construction — no instrument, jurisdiction or authority is named (R-46).
 */
export const SCHEME_TRANSITION_PERIOD_END_RULES = [
    'closed_from_anchor_plus_period',
    'open_through_anchor_plus_period',
];
/** The rule applied when a window declares none — today's behaviour, unchanged. */
export const DEFAULT_SCHEME_TRANSITION_PERIOD_END_RULE = 'closed_from_anchor_plus_period';
/**
 * Why a declared window could not be dated. Each is a real answer and each
 * leaves every date on the row empty — no fallback to the declaring
 * framework's own date, to today, or to any other figure (AC7).
 */
export const TRANSITIONAL_WINDOW_NO_ANCHOR_REASONS = [
    /** The anchoring framework is not installed for this organisation. */
    'anchor_framework_not_installed',
    /** It is installed and states no effective date. */
    'anchor_effective_date_not_stated',
    /** It is installed and its effective date is not a calendar date. */
    'anchor_effective_date_not_a_date',
];
/**
 * Where a declared window stands on the date it was compared against.
 *
 * `no_anchor_declared` is its own state and carries no date at all.
 */
export const TRANSITIONAL_WINDOW_STATES = [
    'not_yet_open',
    'open',
    'closed',
    'no_anchor_declared',
];
/**
 * What a subject's classification looks like against one declared window.
 *
 * - `window_not_yet_open` / `no_anchor_declared` — nothing is asked yet.
 * - `redetermination_due` — the window is open, the subject is classified under
 *   the predecessor and holds no determined level under the successor (an
 *   entry added with no level is not one): a person is prompted.
 * - `successor_recorded` — the window is open and the subject already holds a
 *   determined level under the successor. Nothing is asked. (Gate E12.5 pass 2,
 *   P2-A: this said *"already carries an entry under the successor"*.)
 * - `closed_window` — the window has closed and the predecessor entry stands
 *   un-retired: a person is asked to retire it.
 * - `retired` — a person has retired the predecessor entry.
 * - `window_not_in_use` — the framework that declares the window was installed
 *   for reference (or its record could not be found);
 *   `window.declaration_absence` says which. Nothing is asked
 *   (gate E12.5 pass 1, P3-5).
 * - `successor_not_installed` — the window would ask for a level under the
 *   later scheme, and that scheme is not installed for this organisation. The
 *   registry reports it (`transition_related_scheme_not_installed`); no subject
 *   is prompted to classify under a scheme it cannot use, and no duty is raised
 *   (gate E12.5 pass 1, P3-4).
 */
export const SUBJECT_TRANSITIONAL_STATES = [
    'window_not_yet_open',
    'no_anchor_declared',
    'redetermination_due',
    'successor_recorded',
    'closed_window',
    'retired',
    'window_not_in_use',
    'successor_not_installed',
];
/** The duty a read raised, if any. Named so the page can say what was asked. */
export const TRANSITIONAL_DUTY_KINDS = ['redetermination', 'retirement'];
/** Why a declared relationship was set aside. The scheme itself stays usable. */
export const SCHEME_RELATIONSHIP_DIAGNOSTIC_REASONS = [
    'transition_malformed',
    'transition_related_scheme_is_self',
    'transition_direction_unknown',
    'transition_anchor_malformed',
    'transition_window_malformed',
    /** Sprint 63.33.0 (AC1) — `period_end` is present and not a member of the closed union. */
    'transition_period_end_unknown',
    'transition_prompt_malformed',
    'transition_related_scheme_not_installed',
    'transition_related_scheme_kind_mismatch',
];
// ── Pack selector vocabulary (Sprint 62.20.0) ────────────────────
/**
 * The `event_type` a pack puts on a `grc_regulatory_deadlines` record to
 * declare an upward-reclassification PROMPT (AC4).
 *
 * PLATFORM selector vocabulary in the 64.153.0 tradition — a word this codebase
 * owns, not a word any instrument uses. The prompt's own text, authority and
 * codes are read off the record; nothing in `src/` branches on them.
 *
 * A prompt is deliberately NOT `aggregation_rule: 'highest_component'`. That
 * rule is applied BY THE PLATFORM, and AC4 says the platform must not
 * reclassify on its own. A scheme whose upward move needs a person declares
 * `aggregation_rule: 'none'` and declares this record beside it.
 */
export const CLASSIFICATION_AGGREGATION_PROMPT_EVENT_TYPE = 'classification_aggregation_prompt';
/**
 * Guidance-record `tags` this module selects the pack's own statements by, in
 * the house `boundaryStatements(orgId, tag)` shape.
 */
export const CLASSIFICATION_GUIDANCE_TAGS = {
    /** "This instrument is a draft; nothing under it is owed." */
    draftStatus: 'draft_status',
    /** What a scheme's levels are, and are not, a determination of. */
    schemeBoundary: 'classification_scheme_boundary',
};
// ── Provenance ───────────────────────────────────────────────────
/**
 * Version of the platform-authored part of screening: the proposal rules below,
 * not any instrument's content.
 */
export const CLASSIFICATION_RULESET_VERSION = '62.4.0';
export const CLASSIFICATION_RULE_IDS = [
    'declared_category_match',
    'threshold_evaluation',
    'declared_and_threshold_match',
    'no_match',
];
// ── Review ───────────────────────────────────────────────────────
export const CLASSIFICATION_REVIEW_STATUSES = [
    'pending_review',
    'approved',
    'rejected',
    'superseded',
];
/**
 * Why an assessment stopped being the current one.
 *
 * Sprint 62.4.0, gate E12.5 pass-2 F1. `superseded` was recorded with a pointer
 * to the winner and nothing else, and by this sprint there are two structurally
 * different ways to reach it: a later assessment was **approved** (the reviewer
 * decided, and every earlier approval stops claiming currency), or the pending
 * one had become **undecidable** because the installed list moved under it and a
 * re-screen replaced it without anyone deciding anything.
 *
 * Those are not the same fact about the record. The first says "a person made a
 * later decision"; the second says "no person ever decided this, and none could
 * have". An auditor reading a register of superseded proposals cannot tell them
 * apart from the pointer alone, and the difference is exactly the one the
 * `review` block exists to make visible.
 */
export const CLASSIFICATION_SUPERSEDE_REASONS = [
    /** A later assessment for the same subject was approved. */
    'later_approval',
    /**
     * The pack version behind the scheme changed while this assessment waited, so
     * it could no longer be decided either way, and a re-screen against the
     * current list replaced it.
     */
    'source_version_changed',
    /**
     * The scheme this assessment was screened against is no longer installed at
     * all, so it could no longer be resolved, let alone decided.
     */
    'scheme_no_longer_installed',
    /**
     * The scheme is still installed, and is no longer **valid**.
     *
     * Sprint 62.4.0, gate E12.5 pass-3 F3. A pack upgrade can leave a scheme
     * declared but malformed — a category pointing at a tier the new version
     * dropped, two tiers at one rank, a threshold on a metric that was renamed.
     * `discover()` excludes such a scheme from the usable list and reports it as a
     * named diagnostic, so from a resolver's point of view it looks exactly like
     * an absent one.
     *
     * It is not the same fact. "No longer installed" says the tenant removed the
     * pack; "no longer valid" says the pack is present and its content is broken,
     * which is a defect with a **repair**, and the diagnostic names the framework
     * to repair. Recording the first when the second happened puts a false
     * statement on a permanent compliance record and sends the operator to
     * reinstall something they already have.
     */
    'scheme_no_longer_valid',
    /**
     * Sprint 64.38.0 — **nobody decided anything, and nobody could have.**
     *
     * Two assessments for one subject were left `pending_review` by the
     * read-then-write `createAssessment` used before the partial unique index on
     * `(org_id, subject_profile_id)` filtered to `pending_review`. That pair is
     * what stopped the index building, and an index that does not build withholds
     * the guarantee from every organisation in the deployment, not just the one
     * holding the pair.
     *
     * The migration that resolves the group records this rather than
     * `later_approval`, because the four reasons above all describe something that
     * *happened to the assessment* — a decision, a pack move, a scheme going away.
     * This one describes a defect in the platform's own write path, and an auditor
     * reading the register is entitled to tell the two apart. Nothing about the
     * record's screening, matches, sequence or review history changes; only its
     * currency does, and `superseded_by_assessment_id` names the sibling that
     * remained pending.
     */
    'duplicate_pending_resolution',
];
// ── Subject profiles ─────────────────────────────────────────────
export const CLASSIFICATION_PROFILE_STATUSES = ['active', 'archived'];
/**
 * How the tier now in force was arrived at.
 *
 * `manual_legacy` exists because the free `data_tier` dropdown shipped in 46.1.0
 * and its values are real. A profile created over a registry item already marked
 * `core` must not claim that value was screened; it says `manual_legacy` until
 * someone screens it, which is both honest and what makes the gap visible on
 * screen rather than only in a report nobody runs.
 */
export const CLASSIFICATION_TIER_SOURCES = ['manual_legacy', 'assessed_and_approved'];
// ── Protective measures ──────────────────────────────────────────
export const PROTECTIVE_MEASURE_STATUSES = [
    'not_started',
    'in_progress',
    'implemented',
    'not_applicable',
];
// ── Regulated offerings (data products, services, exchange listings) ──
export const REGULATED_OFFERING_KINDS = [
    'data_product',
    'data_service',
    'data_exchange_listing',
];
export const REGULATED_OFFERING_STATUSES = ['draft', 'active', 'suspended', 'withdrawn'];
// ── Collections ──────────────────────────────────────────────────
export const CLASSIFICATION_SUBJECT_PROFILE_COLLECTION = 'grc_classification_subject_profiles';
export const CLASSIFICATION_ASSESSMENT_COLLECTION = 'grc_classification_assessments';
export const PROTECTIVE_MEASURE_COLLECTION = 'grc_protective_measures';
export const REGULATED_OFFERING_COLLECTION = 'grc_regulated_offerings';
/**
 * Testing a protective measure — Sprint 65.7.0 (AC3).
 *
 * ## Why a `ProtectiveMeasureRecord` was not enough
 *
 * `ProtectiveMeasureRecord` carries `PROTECTIVE_MEASURE_STATUSES`, one of which
 * is `implemented`. Until this sprint that was the end of the story: a grep for
 * `tested_at`, `test_result`, `effectiveness` and `next_test_due_at` over that
 * interface returned nothing at all. Nothing recorded that a control had been
 * exercised, by whom, against what, with what result, or when it was next due.
 * **`implemented` is what somebody typed**, and Sprint 65.0.0's exit contract
 * says it in three words — *a checkbox never proves operation*.
 *
 * A test is a separate record rather than more fields on the measure because a
 * control is tested repeatedly and each test is its own evidence. Collapsing
 * them onto the measure would keep only the last one and silently discard the
 * history an auditor asks for first.
 *
 * ## What this is NOT
 *
 * It is **not a control catalogue**. `protective-measure-applicability.ts`
 * forbids one in as many words — *the platform ships no control catalogue … a
 * catalogue would be a fourth vocabulary nobody legislated and would have to be
 * re-authored per jurisdiction*. The control under test is the installed
 * framework requirement the measure already names in `source`. This adds a test
 * OF a control, never a control.
 */
export const PROTECTIVE_MEASURE_TEST_COLLECTION = 'grc_protective_measure_tests';
/**
 * How the control was examined.
 *
 * Deliberately the generic assurance vocabulary rather than any instrument's:
 * no framework, no jurisdiction and no authority names these, so R-46 is not
 * engaged and the list does not have to be re-authored per pack.
 */
export const PROTECTIVE_MEASURE_TEST_METHODS = [
    'inspection',
    'observation',
    'reperformance',
    'enquiry',
    'automated_check',
];
/**
 * What the test concluded.
 *
 * `inconclusive` exists so that a test which could not reach an answer is
 * recordable as itself. Without it the only way to close a test that proved
 * nothing is to call the control effective, which is the failure this whole
 * record exists to stop — and it is the same reason `not_applicable` on a
 * measure carries a mandatory rationale.
 *
 * There is no `passed`. A control is `effective` or it is not; a word that
 * sounds like a grade invites one.
 */
export const PROTECTIVE_MEASURE_TEST_RESULTS = [
    'effective',
    'deficient',
    'not_effective',
    'inconclusive',
];
/** The results that leave a control standing. Everything else needs remediation. */
export const PROTECTIVE_MEASURE_TEST_RESULTS_SATISFACTORY = ['effective'];
// ── Errors ───────────────────────────────────────────────────────
/**
 * Every error this module can produce.
 *
 * One code per distinct condition. Sprint 62.4.0's gate E12.5 found four call
 * sites reusing `SUBJECT_NOT_FOUND` for conditions that are not a missing
 * subject — a missing offering, a missing measure, a dangling link, an
 * uninstalled framework — and `SCHEME_NOT_FOUND` thrown for a missing
 * *assessment*. A code that means five things is a distinction no consumer can
 * make, so each condition gets its own; a guard test asserts every code below is
 * thrown somewhere in the module.
 *
 * Gate E12.5 pass-4 F8 corrected the justification that used to sit here. It
 * read "The frontend branches on codes", and `grep` finds **zero** frontend
 * consumers of any code in this block — no page, hook or client reads one. The
 * codes are still worth having, for the consumers that do exist: API clients
 * integrating against the module, and support staff reading a payload to tell a
 * bad request from broken installed content. What was wrong was the reason
 * given, not the practice, and a wrong reason invites the wrong repair — a
 * reader who checked would have concluded the codes were unused and could be
 * collapsed.
 */
export const CLASSIFICATION_ERROR_CODES = {
    NO_SCHEME_INSTALLED: 'no_classification_scheme_installed',
    SCHEME_NOT_FOUND: 'classification_scheme_not_found',
    /**
     * An installed scheme's own content is malformed — a `409`, meaning *repair
     * the pack*. Never a bad value from the caller: see `SCOPE_NOT_DECLARED`.
     */
    SCHEME_INVALID: 'classification_scheme_invalid',
    /**
     * The caller named a scope the scheme does not declare — a `400`, meaning
     * *send a different value*.
     *
     * Gate E12.5 pass-4 F8. This threw `SCHEME_INVALID`, which everywhere else in
     * the module means the installed pack content is broken and is raised at
     * `409`. One code across two statuses left a consumer unable to tell "your
     * request was wrong" from "this tenant's installed content is wrong" — the
     * first is fixed by changing a request, the second by repairing a pack and
     * cannot be retried. That is precisely the ambiguity the one-code-per-
     * condition rule at the top of this block exists to prevent, and it was the
     * only remaining code in the module carrying two unrelated meanings.
     */
    SCOPE_NOT_DECLARED: 'classification_scope_not_declared',
    SCHEME_KIND_MISMATCH: 'classification_scheme_kind_mismatch',
    UNKNOWN_CATEGORY: 'classification_unknown_category',
    UNKNOWN_TIER: 'classification_unknown_tier',
    UNKNOWN_METRIC: 'classification_unknown_metric',
    /** No such subject in this organisation — a `404`. */
    SUBJECT_NOT_FOUND: 'classification_subject_not_found',
    /**
     * The scheme names a subject *kind* this platform has no collection for — a
     * `400`.
     *
     * Found by the pass-4 F8 guard rather than by the finding: widening F8's
     * repair into "no code is thrown at two statuses" immediately turned up a
     * second instance of the same shape. `SUBJECT_NOT_FOUND` was raised both here
     * (the kind is unsupported, so no id of any value could resolve — the pack or
     * the request names something the platform does not model) and at `404` for a
     * subject that genuinely does not exist. Retrying with a different id fixes
     * the second and can never fix the first.
     */
    SUBJECT_KIND_UNSUPPORTED: 'classification_subject_kind_unsupported',
    PROFILE_EXISTS: 'classification_profile_exists',
    ASSESSMENT_NOT_FOUND: 'classification_assessment_not_found',
    ASSESSMENT_NOT_PENDING: 'assessment_not_pending_review',
    STALE_ASSESSMENT_REVIEW: 'stale_assessment_review',
    /**
     * The installed list moved under a pending assessment.
     *
     * Gate E12.5 F5. The review path resolved the **currently installed** scheme
     * while the record — and the detail page the reviewer is reading — carry the
     * pack version captured at screening time. Upgrade the pack while an
     * assessment waits and the reviewer decides against a different list from the
     * one on screen, with the record recording the old version as the basis. The
     * decision is refused and a re-screen is directed, because a classification
     * bound to a version it was not evaluated against is exactly what the source
     * block exists to prevent.
     */
    SOURCE_VERSION_CHANGED: 'assessment_source_version_changed',
    /** A reviewer excepted a category the assessment never matched. */
    EXCEPTION_NOT_MATCHED: 'classification_exception_not_matched',
    /**
     * A `:write` holder tried to repoint a profile whose tier is in force.
     *
     * Gate E12.5 F15: `current_tier_code` means something only relative to the
     * scheme it was approved under, so moving the scheme leaves a tier code
     * pointing at another instrument's vocabulary — with no `:review` and no audit
     * of the classification change.
     */
    PROFILE_CLASSIFIED: 'classification_profile_already_classified',
    MEASURE_NOT_FOUND: 'protective_measure_not_found',
    RATIONALE_REQUIRED: 'protective_measure_rationale_required',
    /**
     * Sprint 65.7.0 (AC3) — a test recorded with any result other than
     * `effective` carries no finding. A `400`.
     *
     * The twin of `RATIONALE_REQUIRED` and for the same reason: without it,
     * `deficient` is a status change wearing a test's clothes — a control marked
     * as failing with nothing said about *how*, which is unactionable by the
     * owner and unreadable by an auditor. `effective` needs no finding because
     * the result is the finding.
     */
    TEST_FINDINGS_REQUIRED: 'protective_measure_test_findings_required',
    /**
     * Sprint 65.7.0 (AC3) — `performed_at` names a moment that has not happened.
     * A `400`.
     *
     * Distinct from `INVALID_DATE`, which means the value is not a timestamp at
     * all. This one IS a well-formed instant, and the two have different
     * remedies: reformat the value, or record the test after performing it.
     * A control cannot have been examined tomorrow, and a register that accepts
     * one cannot be read as evidence of anything.
     */
    TEST_PERFORMED_IN_FUTURE: 'protective_measure_test_performed_in_future',
    /**
     * Sprint 65.7.0 (AC3) — `scope_examined` is blank once trimmed. A `400`.
     *
     * "What was examined" is the field that separates a test from an assertion,
     * so a whitespace answer is refused rather than stored. Zod's `min(1)` at the
     * route accepts `" "`; this is the rule, and the route schema is the
     * cheap first pass in front of it.
     */
    TEST_SCOPE_REQUIRED: 'protective_measure_test_scope_required',
    OFFERING_NOT_FOUND: 'regulated_offering_not_found',
    /** A registry item, system, data flow, vendor, incident or response id that resolves to nothing. */
    LINK_NOT_FOUND: 'classification_link_not_found',
    OWNER_NOT_FOUND: 'classification_owner_not_found',
    FRAMEWORK_NOT_INSTALLED: 'classification_framework_not_installed',
    REQUIREMENT_NOT_FOUND: 'classification_requirement_not_found',
    INVALID_DATE: 'classification_invalid_date',
    /**
     * Sprint 62.20.0 — two of a subject's entries name schemes that each declare
     * a `platform_data_tier`. A `409`: repair the installed content.
     *
     * Fail-closed rather than resolved. Both entries would write
     * `DataRegistryItem.data_tier` and the last approval would win silently —
     * which is verbatim the defect the collection's unique index exists to
     * prevent, arriving through the widened profile instead of through a second
     * profile. The platform cannot pick between two instruments' vocabularies,
     * and picking one is worse than refusing, because a wrong `data_tier` drives
     * pre-transfer filing, the annual dossier and log retention.
     */
    MULTIPLE_LEGACY_TIER_SCHEMES: 'classification_multiple_legacy_tier_schemes',
    /**
     * Sprint 62.20.0 — the caller tried to set a tier through the profile patch.
     * A `400`, naming the path that does write one.
     */
    TIER_REQUIRES_REVIEW: 'classification_tier_requires_review',
    /** A cross-mapping with fewer than two legs, or two legs on one scheme. */
    CROSS_MAPPING_INVALID: 'classification_cross_mapping_invalid',
    /** No installed record declares the prompt code the caller answered. */
    AGGREGATION_PROMPT_NOT_FOUND: 'classification_aggregation_prompt_not_found',
    /**
     * The prompt decision names a scheme this subject carries no entry for.
     * Distinct from `SCHEME_NOT_FOUND` (the scheme is not installed) and from
     * `SUBJECT_NOT_FOUND` (there is no such profile): the scheme is installed and
     * the profile exists, and the entry is what is missing.
     */
    CLASSIFICATION_ENTRY_NOT_FOUND: 'classification_entry_not_found',
    /**
     * Sprint 62.26.0 (AC9) — a retirement names a scheme that no installed,
     * well-formed transitional relationship declares as a predecessor. A `400`.
     *
     * Retirement answers a pack-declared transition; it is not a general way to
     * set an entry aside, and a request naming any other scheme is refused
     * rather than recorded against a window that does not exist.
     */
    TRANSITIONAL_RELATIONSHIP_NOT_FOUND: 'classification_transitional_relationship_not_found',
    /** Sprint 62.26.0 (AC9) — the entry already carries a retirement. A `409`. */
    TRANSITIONAL_ENTRY_ALREADY_RETIRED: 'classification_transitional_entry_already_retired',
    /**
     * Sprint 62.26.0 (AC9) — `retired_on` names a day that has not come yet in the
     * organisation's calendar. A `400`. A retirement is a statement of what a
     * person has done, and one dated tomorrow is not yet true.
     */
    TRANSITIONAL_RETIREMENT_IN_FUTURE: 'classification_transitional_retirement_in_future',
    /** Sprint 62.26.0 (AC9) — the rationale is blank once trimmed. A `400`. */
    TRANSITIONAL_RETIREMENT_RATIONALE_REQUIRED: 'classification_transitional_retirement_rationale_required',
};
//# sourceMappingURL=regulatory-classification.js.map